<script setup>
import { ref, watch, onMounted } from 'vue';
import ModalWrapper from './ModalWrapper.vue';
import { rpc } from '../../../composables/useRpc.js';
import { SUPABASE_URL } from '../../../composables/useSupabase.js';

const STORAGE_BASE = `${SUPABASE_URL}/storage/v1/object/public/`;

function flattenWorks(worksData) {
  const photos = [];
  for (const work of (worksData || [])) {
    for (const photo of (work.photos || [])) {
      photos.push({
        url: STORAGE_BASE + photo.storage_path,
        caption: work.title || '',
        workId: work.id,
      });
    }
  }
  return photos;
}

const props = defineProps({
  show: { type: Boolean, default: false },
  phone: { type: String, default: '' },
  conversationId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close']);

const categories = ref([]);
const selectedCategory = ref('');
const searchQuery = ref('');
const works = ref([]);
const selected = ref([]);
const loadingWorks = ref(false);
const sending = ref(false);
const sendProgress = ref('');
const error = ref('');
const includeCaption = ref(true);
let debounceTimer = null;

watch(() => props.show, (val) => {
  if (val) {
    loadCategories();
    works.value = [];
    selected.value = [];
    selectedCategory.value = '';
    searchQuery.value = '';
    sendProgress.value = '';
    error.value = '';
  }
});

watch(selectedCategory, (cat) => {
  if (cat) {
    searchQuery.value = '';
    loadWorks(cat);
  } else if (!searchQuery.value.trim()) {
    works.value = [];
  }
  selected.value = [];
});

// Debounced search as user types
watch(searchQuery, (q) => {
  clearTimeout(debounceTimer);
  if (!q.trim()) {
    if (!selectedCategory.value) works.value = [];
    return;
  }
  selectedCategory.value = '';
  debounceTimer = setTimeout(() => searchByText(q.trim()), 400);
});

onMounted(() => {
  if (props.show) loadCategories();
});

async function loadCategories() {
  try {
    const data = await rpc('portfolio-categories');
    categories.value = data || [];
  } catch (e) {
    error.value = 'Error cargando categorías';
  }
}

async function loadWorks(category) {
  loadingWorks.value = true;
  error.value = '';
  try {
    const data = await rpc(`portfolio?category=${encodeURIComponent(category)}&limit=40`);
    works.value = flattenWorks(data);
  } catch (e) {
    error.value = 'Error cargando portfolio';
  } finally {
    loadingWorks.value = false;
  }
}

async function searchByText(q) {
  const query = q || searchQuery.value.trim();
  if (!query) return;
  loadingWorks.value = true;
  error.value = '';
  selected.value = [];
  try {
    const data = await rpc(`portfolio?query=${encodeURIComponent(query)}&limit=40`);
    works.value = flattenWorks(data);
  } catch (e) {
    error.value = 'Error buscando en portfolio';
  } finally {
    loadingWorks.value = false;
  }
}

function toggleSelect(idx) {
  const pos = selected.value.indexOf(idx);
  if (pos >= 0) selected.value.splice(pos, 1);
  else selected.value.push(idx);
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function sendSelection() {
  if (!selected.value.length) return;
  sending.value = true;
  error.value = '';
  const total = selected.value.length;

  for (let i = 0; i < total; i++) {
    const photo = works.value[selected.value[i]];
    sendProgress.value = `Enviando ${i + 1}/${total}...`;
    try {
      await rpc('send-image', {
        body: {
          phone: props.phone,
          conversation_id: props.conversationId,
          url: photo.url,
          caption: includeCaption.value ? (photo.caption || '') : '',
        },
      });
    } catch (e) {
      error.value = `Error enviando imagen ${i + 1}`;
      break;
    }
    if (i < total - 1) await delay(1000);
  }

  if (!error.value) {
    sendProgress.value = `${total} enviadas`;
    setTimeout(() => { emit('close'); }, 1200);
  }
  sending.value = false;
}
</script>

<template>
  <ModalWrapper :show="show" title="Portfolio" @close="emit('close')">
    <div class="aj-pf-filter">
      <input
        v-model="searchQuery"
        class="aj-pf-search"
        type="text"
        placeholder="Buscar por nombre..."
        autofocus
      />
      <select
        v-model="selectedCategory"
        class="aj-pf-select"
      >
        <option value="">O filtrar por categoría</option>
        <option
          v-for="cat in categories"
          :key="cat.slug || cat.name"
          :value="cat.slug || cat.name"
        >
          {{ cat.name || cat.slug }}
        </option>
      </select>
    </div>

    <div class="aj-pf-content">
      <div v-if="loadingWorks" class="aj-pf-loading">
        <span class="aj-spinner" />
      </div>

      <div v-else-if="works.length" class="aj-pf-grid">
        <div
          v-for="(photo, idx) in works"
          :key="photo.url"
          class="aj-pf-thumb"
          :class="{ 'aj-pf-thumb--selected': selected.includes(idx) }"
          @click="toggleSelect(idx)"
        >
          <img :src="photo.url" :alt="photo.caption || ''" loading="lazy" />
          <div class="aj-pf-check">
            <input
              type="checkbox"
              :checked="selected.includes(idx)"
              @click.stop
              @change="toggleSelect(idx)"
            />
          </div>
          <div class="aj-pf-caption">{{ photo.caption }}</div>
        </div>
      </div>

      <div v-else-if="(selectedCategory || searchQuery.trim()) && !loadingWorks" class="aj-pf-empty">
        Sin fotos encontradas
      </div>

      <div v-else class="aj-pf-empty">
        Escribí para buscar o elegí una categoría
      </div>
    </div>

    <div v-if="error" class="aj-pf-error">{{ error }}</div>

    <div v-if="sendProgress" class="aj-pf-progress">{{ sendProgress }}</div>

    <div class="aj-pf-footer">
      <label class="aj-pf-caption-toggle">
        <input type="checkbox" v-model="includeCaption" />
        <span>Incluir nombre</span>
      </label>
      <button
        class="aj-btn aj-btn-primary"
        :disabled="!selected.length || sending"
        @click="sendSelection"
      >
        {{ sending ? sendProgress : `Enviar selección (${selected.length})` }}
      </button>
    </div>
  </ModalWrapper>
</template>

<style scoped>
.aj-pf-filter {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.aj-pf-search {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgb(var(--slate-5, 224 224 230));
  border-radius: 8px;
  background: rgb(var(--slate-1, 252 252 253));
  color: rgb(var(--slate-12, 28 32 36));
  font-size: 13px;
  height: 36px;
  outline: none;
  box-sizing: border-box;
}

.aj-pf-search:focus {
  border-color: rgb(var(--blue-8, 62 99 221));
  box-shadow: 0 0 0 1px rgb(var(--blue-8, 62 99 221));
}

.aj-pf-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgb(var(--slate-5, 224 224 230));
  border-radius: 8px;
  background: rgb(var(--slate-1, 252 252 253));
  color: rgb(var(--slate-12, 28 32 36));
  font-size: 13px;
  height: 36px;
  cursor: pointer;
  appearance: auto;
}

.aj-pf-select:focus {
  outline: none;
  border-color: rgb(var(--blue-8, 62 99 221));
  box-shadow: 0 0 0 1px rgb(var(--blue-8, 62 99 221));
}

.aj-pf-content {
  height: 320px;
  overflow-y: auto;
  margin-bottom: 12px;
}

.aj-pf-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.aj-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgb(var(--slate-4, 232 232 236));
  border-top-color: rgb(var(--blue-9, 62 99 221));
  border-radius: 50%;
  animation: aj-spin 0.6s linear infinite;
}

@keyframes aj-spin {
  to { transform: rotate(360deg); }
}

.aj-pf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.aj-pf-thumb {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  aspect-ratio: 1;
  transition: border-color 0.15s;
}

.aj-pf-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: rgb(var(--slate-2, 248 248 249));
}

.aj-pf-thumb--selected {
  border-color: rgb(var(--blue-9, 62 99 221));
}

.aj-pf-check {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.aj-pf-check input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.aj-pf-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4px 6px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aj-pf-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 12px;
  color: rgb(var(--slate-9, 142 142 147));
  font-style: italic;
}

.aj-pf-error {
  font-size: 12px;
  color: rgb(var(--red-9, 229 72 77));
  margin-bottom: 8px;
}

.aj-pf-progress {
  font-size: 12px;
  color: rgb(var(--blue-9, 62 99 221));
  margin-bottom: 8px;
  font-weight: 500;
}

.aj-pf-footer {
  padding-top: 10px;
  border-top: 1px solid rgb(var(--slate-4, 232 232 236));
}

.aj-pf-caption-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgb(var(--slate-11, 96 100 108));
  margin-bottom: 8px;
  cursor: pointer;
}

.aj-pf-caption-toggle input {
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.aj-btn {
  width: 100%;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  min-height: 44px;
}

.aj-btn-primary {
  background: rgb(var(--blue-9, 62 99 221));
  color: #fff;
}

.aj-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
